<template>
  <div class="dashboard-general-container">
    <v-card-text v-if="!isLoading && hasDashboardData">
        <!-- KPI Section -->
        <v-row v-if="hasTotalKpisData">
          <v-col cols="12" sm="6" md="3">
            <v-card class="elevation-12 rounded-top kpi-card">
              <v-card-text class="text-center">
                <div class="kpi-icon green lighten-4 green--text text--darken-3">
                  <v-icon large>mdi-cash-plus</v-icon>
                </div>
                <div class="kpi-title">Ingresos Totales</div>
                <div class="kpi-value">{{ formatCurrency(filteredTotalKpis.ingresos_totales) }}</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-card class="elevation-12 rounded-top kpi-card">
              <v-card-text class="text-center">
                <div class="kpi-icon red lighten-4 red--text text--darken-3">
                  <v-icon large>mdi-cash-minus</v-icon>
                </div>
                <div class="kpi-title">Gastos Totales</div>
                <div class="kpi-value">{{ formatCurrency(filteredTotalKpis.gastos_totales) }}</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-card class="elevation-12 rounded-top kpi-card">
              <v-card-text class="text-center">
                <div class="kpi-icon blue lighten-4 blue--text text--darken-3">
                  <v-icon large>mdi-chart-line</v-icon>
                </div>
                <div class="kpi-title">Beneficio/Pérdida</div>
                <div class="kpi-value" :class="Number.isFinite(Number(filteredTotalKpis.beneficio_perdida)) && Number(filteredTotalKpis.beneficio_perdida) >= 0 ? 'green--text' : 'red--text'">
                  {{ formatCurrency(filteredTotalKpis.beneficio_perdida) }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-card class="elevation-12 rounded-top kpi-card">
              <v-card-text class="text-center">
                <div class="kpi-icon purple lighten-4 purple--text text--darken-3">
                  <v-icon large>mdi-percent</v-icon>
                </div>
                <div class="kpi-title">Margen Neto</div>
                <div class="kpi-value">{{ Number.isFinite(Number(filteredTotalKpis.margen_neto)) ? Number(filteredTotalKpis.margen_neto).toFixed(2) : '0.00' }}%</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Charts Section -->
        <!-- Row 1: Monthly Evolution Chart -->
        <v-row v-if="hasMonthlyKpisData">
          <v-col cols="12">
            <v-card class="elevation-12 rounded-top inner-dashboard-card">
              <v-card-title>
                Evolución Mensual
                <span v-if="fiscalYearsText" class="subtitle-1 ml-2">
                  {{ fiscalYearsText }}
                </span>
                <v-spacer></v-spacer>
                <v-btn-toggle v-model="chartView" mandatory>
                  <v-btn small value="ingresos">Ingresos</v-btn>
                  <v-btn small value="gastos">Gastos</v-btn>
                  <v-btn small value="beneficio">Beneficio</v-btn>
                  <v-btn small value="todos">Todos</v-btn>
                </v-btn-toggle>
              </v-card-title>
              <v-card-text>
                <apexchart
                    type="line"
                    height="350"
                    :options="monthlyChartOptions"
                    :series="monthlyChartSeries"
                ></apexchart>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Row 2: Top Expenses and Top Income Charts -->
        <v-row>
          <!-- Top Expenses Pie Chart -->
          <v-col :cols="12" :md="hasIncomeData && hasExpensesData ? 6 : 12" v-if="hasExpensesData">
            <v-card class="elevation-12 rounded-top chart-card inner-dashboard-card">
              <v-card-title>
                Top Gastos
                <span v-if="selectedMonthText" class="subtitle-1 ml-2">
                  {{ selectedMonthText }}
                </span>
                <span v-if="fiscalYearsText" class="subtitle-1 ml-2">
                  {{ fiscalYearsText }}
                </span>
              </v-card-title>
              <v-card-text class="chart-container">
                <apexchart
                    v-if="filteredExpensesData.length > 0"
                    type="pie"
                    height="350"
                    :options="expensesChartOptions"
                    :series="expensesChartSeries"
                    class="chart-element"
                ></apexchart>
                <div v-else class="no-data-message">
                  <v-icon large color="grey lighten-1">mdi-chart-pie</v-icon>
                  <div class="no-data-text">
                    No hay datos disponibles para los filtros seleccionados
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Top Income Bar Chart -->
          <v-col :cols="12" :md="hasIncomeData && hasExpensesData ? 6 : 12" v-if="hasIncomeData">
            <v-card class="elevation-12 rounded-top chart-card inner-dashboard-card">
              <v-card-title>
                Top Ingresos
                <span v-if="selectedMonthText" class="subtitle-1 ml-2">
                  {{ selectedMonthText }}
                </span>
                <span v-if="fiscalYearsText" class="subtitle-1 ml-2">
                  {{ fiscalYearsText }}
                </span>
              </v-card-title>
              <v-card-text class="chart-container">
                <apexchart
                    v-if="filteredIncomeData.length > 0"
                    type="bar"
                    height="350"
                    :options="incomeChartOptions"
                    :series="incomeChartSeries"
                    class="chart-element"
                ></apexchart>
                <div v-else class="no-data-message">
                  <v-icon large color="grey lighten-1">mdi-chart-bar</v-icon>
                  <div class="no-data-text">
                    No hay datos disponibles para los filtros seleccionados
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Row 3: Top Clientes y Proveedores Charts (from clientesProveedores data) -->
        <v-row v-if="hasClientesProveedoresData">
          <!-- Top Proveedores Pie Chart -->
          <v-col :cols="12" :md="hasClientesProveedoresIncomeData && hasClientesProveedoresExpensesData ? 6 : 12" v-if="hasClientesProveedoresExpensesData">
            <v-card class="elevation-12 rounded-top chart-card inner-dashboard-card">
              <v-card-title>
                Top Proveedores
                <span v-if="selectedMonthText" class="subtitle-1 ml-2">
                  {{ selectedMonthText }}
                </span>
                <span v-if="fiscalYearsText" class="subtitle-1 ml-2">
                  {{ fiscalYearsText }}
                </span>
              </v-card-title>
              <v-card-text class="chart-container">
                <apexchart
                    v-if="filteredClientesProveedoresExpensesData.length > 0"
                    type="pie"
                    height="350"
                    :options="clientesProveedoresExpensesChartOptions"
                    :series="clientesProveedoresExpensesChartSeries"
                    class="chart-element"
                ></apexchart>
                <div v-else class="no-data-message">
                  <v-icon large color="grey lighten-1">mdi-chart-pie</v-icon>
                  <div class="no-data-text">
                    No hay datos disponibles para los filtros seleccionados
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Top Clientes Bar Chart -->
          <v-col :cols="12" :md="hasClientesProveedoresIncomeData && hasClientesProveedoresExpensesData ? 6 : 12" v-if="hasClientesProveedoresIncomeData">
            <v-card class="elevation-12 rounded-top chart-card inner-dashboard-card">
              <v-card-title>
                Top Clientes
                <span v-if="selectedMonthText" class="subtitle-1 ml-2">
                  {{ selectedMonthText }}
                </span>
                <span v-if="fiscalYearsText" class="subtitle-1 ml-2">
                  {{ fiscalYearsText }}
                </span>
              </v-card-title>
              <v-card-text class="chart-container">
                <apexchart
                    v-if="filteredClientesProveedoresIncomeData.length > 0"
                    type="bar"
                    height="350"
                    :options="clientesProveedoresIncomeChartOptions"
                    :series="clientesProveedoresIncomeChartSeries"
                    class="chart-element"
                ></apexchart>
                <div v-else class="no-data-message">
                  <v-icon large color="grey lighten-1">mdi-chart-bar</v-icon>
                  <div class="no-data-text">
                    No hay datos disponibles para los filtros seleccionados
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Row 4: Recent Invoices Table -->
        <v-row v-if="hasInvoicesData">
          <v-col cols="12">
            <v-card class="elevation-12 rounded-top inner-dashboard-card">
              <v-card-title>
                Últimas Facturas
                <v-spacer></v-spacer>
    <!--            <v-text-field-->
    <!--                v-model="search"-->
    <!--                append-icon="mdi-magnify"-->
    <!--                label="Buscar"-->
    <!--                single-line-->
    <!--                hide-details-->
    <!--                dense-->
    <!--            ></v-text-field>-->
              </v-card-title>
              <v-card-text>
                <v-data-table
                    :headers="invoiceHeaders"
                    :items="dashboardData.ultimasFacturas"
                    :search="search"
                    :items-per-page="5"
                    class="elevation-1"
                    @click:row="selectRow"
                >
                  <template v-slot:item.tipo="{ item }">
                    <v-chip
                        :color="getTipoColor(item.tipo)"
                        text-color="white"
                        small
                    >
                      {{ item.tipo }}
                    </v-chip>
                  </template>
                  <template v-slot:item.importe="{ item }">
                    {{ formatCurrency(item.importe) }}
                  </template>
                  <template v-slot:item.estado="{ item }">
                    <v-chip
                        :color="item.estado === 'Contabilizada' ? 'green' : 'orange'"
                        text-color="white"
                        small
                    >
                      {{ item.estado }}
                    </v-chip>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

    <!-- Empty State - No Dashboard Data -->
    <div v-if="!isLoading && !hasDashboardData" class="centered-content pa-8">
      <v-icon large color="grey lighten-1">mdi-view-dashboard</v-icon>
      <h2 class="mt-4 grey--text text--darken-1">No hay datos disponibles</h2>
      <p class="mt-2 text--secondary">
        No se encontraron datos para el ejercicio seleccionado
      </p>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';

export default {
  name: 'DashboardGeneral',

  components: {
    apexchart: VueApexCharts
  },

  props: {
    dashboardData: {
      type: Object,
      default: null
    },
    selectedFiscalYear: {
      type: Number,
      default: null
    },
    selectedFiscalYears: {
      type: Array,
      default: () => []
    },
    selectedMonth: {
      type: String,
      default: 'todos'
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      chartView: 'todos',
      search: '',
      invoiceHeaders: [
        { text: 'Fecha', value: 'fecha' },
        { text: 'ID', value: 'id' },
        { text: 'Tipo', value: 'tipo' },
        { text: 'Tercero', value: 'origen' },
        { text: 'Importe', value: 'importe' },
        { text: 'Estado', value: 'estado' }
      ]
    };
  },

  computed: {
    // Data availability checks for conditional rendering
    hasTotalKpisData() {
      // Check if dashboardData and monthlyKpis exist
      if (!this.dashboardData ||
          !this.dashboardData.monthlyKpis ||
          !this.dashboardData.monthlyKpis.length) {
        return false;
      }

      // If we have monthlyKpis data, we can calculate totalKpis
      // The actual calculation is done in filteredTotalKpis computed property
      return true;
    },

    // Determinar si hay múltiples ejercicios seleccionados
    hasMultipleFiscalYears() {
      return this.selectedFiscalYears && this.selectedFiscalYears.length > 1;
    },

    // Texto para mostrar los ejercicios seleccionados
    fiscalYearsText() {
      if (!this.selectedFiscalYears || this.selectedFiscalYears.length === 0) {
        return this.selectedFiscalYear ? String(this.selectedFiscalYear) : '';
      }
      if (this.selectedFiscalYears.length === 1) {
        return String(this.selectedFiscalYears[0]);
      }
      // Múltiples ejercicios: mostrar como rango o lista
      const sorted = [...this.selectedFiscalYears].sort((a, b) => a - b);
      return `${sorted[0]} - ${sorted[sorted.length - 1]}`;
    },


    // Filtered totalKpis based on selected fiscal year and month
    filteredTotalKpis() {
      // First, check if dashboardData and monthlyKpis exist
      if (!this.dashboardData || !this.dashboardData.monthlyKpis || this.dashboardData.monthlyKpis.length === 0) {
        return {
          ejercicio: this.selectedFiscalYear || new Date().getFullYear(),
          ingresos_totales: 0,
          gastos_totales: 0,
          beneficio_perdida: 0,
          margen_neto: 0
        };
      }

      let filteredMonthlyData;

      // Si hay múltiples ejercicios, los datos ya vienen filtrados del padre - NO filtrar más
      if (this.hasMultipleFiscalYears) {
        filteredMonthlyData = this.dashboardData.monthlyKpis;
      } else {
        // Filtrar por ejercicio único
        filteredMonthlyData = this.dashboardData.monthlyKpis.filter(item => {
          if (!item.mes_ejercicio || typeof item.mes_ejercicio !== 'string') {
            return false;
          }

          try {
            const [, year] = item.mes_ejercicio.split('/');
            const yearNum = parseInt(year);
            return this.selectedFiscalYear === null || yearNum === this.selectedFiscalYear;
          } catch (error) {
            console.error('Error parsing mes_ejercicio:', error, item);
            return false;
          }
        });
      }

      // Further filter by selected month if not 'todos' and not using month range filter
      if (this.selectedMonth !== 'todos' && !this.hasMultipleFiscalYears) {
        filteredMonthlyData = filteredMonthlyData.filter(item => {
          return this.monthMatchesFilter(item.mes_ejercicio);
        });
      }
      
      // If no data after filtering, return default object
      if (filteredMonthlyData.length === 0) {
        return {
          ejercicio: this.selectedFiscalYear || new Date().getFullYear(),
          ingresos_totales: 0,
          gastos_totales: 0,
          beneficio_perdida: 0,
          margen_neto: 0
        };
      }
      
      // Calculate totals from filtered monthly data
      const ingresos_totales = filteredMonthlyData.reduce((sum, item) => sum + (item.ingresos || 0), 0);
      const gastos_totales = filteredMonthlyData.reduce((sum, item) => sum + (item.gastos || 0), 0);
      const beneficio_perdida = ingresos_totales - gastos_totales;
      
      // Calculate margen_neto (as a percentage)
      let margen_neto = 0;
      if (ingresos_totales > 0) {
        margen_neto = (beneficio_perdida / ingresos_totales) * 100;
      }
      
      return {
        ejercicio: this.selectedFiscalYear || new Date().getFullYear(),
        ingresos_totales,
        gastos_totales,
        beneficio_perdida,
        margen_neto: parseFloat(margen_neto.toFixed(2))
      };
    },
    
    hasMonthlyKpisData() {
      return this.dashboardData && 
             this.dashboardData.monthlyKpis && 
             this.dashboardData.monthlyKpis.length > 0;
    },
    
    hasExpensesData() {
      return this.dashboardData && 
             this.dashboardData.gastosIngresos && 
             this.dashboardData.gastosIngresos.filter(item => item.is_gasto).length > 0;
    },
    
    hasIncomeData() {
      return this.dashboardData && 
             this.dashboardData.gastosIngresos && 
             this.dashboardData.gastosIngresos.filter(item => !item.is_gasto).length > 0;
    },
    
    hasInvoicesData() {
      return this.dashboardData && 
             this.dashboardData.ultimasFacturas && 
             this.dashboardData.ultimasFacturas.length > 0;
    },
    
    hasClientesProveedoresData() {
      return this.dashboardData && 
             this.dashboardData.clientesProveedores && 
             this.dashboardData.clientesProveedores.length > 0;
    },
    
    hasClientesProveedoresExpensesData() {
      return this.dashboardData && 
             this.dashboardData.clientesProveedores && 
             this.dashboardData.clientesProveedores.filter(item => item.is_gasto).length > 0;
    },
    
    hasClientesProveedoresIncomeData() {
      return this.dashboardData && 
             this.dashboardData.clientesProveedores && 
             this.dashboardData.clientesProveedores.filter(item => !item.is_gasto).length > 0;
    },
    
    // Check if any dashboard data exists
    hasDashboardData() {
      return this.hasTotalKpisData || 
             this.hasMonthlyKpisData || 
             this.hasExpensesData || 
             this.hasIncomeData ||
             this.hasClientesProveedoresData;
    },
    
    // Note: availableFiscalYears is provided by parent via selectedFiscalYear prop
    // The parent component manages the fiscal year selection
    
    // Month/quarter options for the month selector
    monthOptions() {
      // Create an array with "Todos" and quarters
      const options = [
        { text: 'Todos', value: 'todos' },
      ];
      
      // Add individual months
      const months = [
        { text: 'Enero', value: '1' },
        { text: 'Febrero', value: '2' },
        { text: 'Marzo', value: '3' },
        { text: 'Abril', value: '4' },
        { text: 'Mayo', value: '5' },
        { text: 'Junio', value: '6' },
        { text: 'Julio', value: '7' },
        { text: 'Agosto', value: '8' },
        { text: 'Septiembre', value: '9' },
        { text: 'Octubre', value: '10' },
        { text: 'Noviembre', value: '11' },
        { text: 'Diciembre', value: '12' }
      ];
      // Add quarters
      const quarters = [
        { text: 'Trimestre 1', value: 'q1' },
        { text: 'Trimestre 2', value: 'q2' },
        { text: 'Trimestre 3', value: 'q3' },
        { text: 'Trimestre 4', value: 'q4' }
      ];
      
      return [...options, ...months, ...quarters];
    },
    
    
    // Get display text for the selected month/quarter
    selectedMonthText() {
      if (this.selectedMonth === 'todos') {
        return '';
      }
      
      // Find the selected month option
      const option = this.monthOptions.find(opt => opt.value === this.selectedMonth);
      return option ? option.text : '';
    },
    
    // Filtered expenses data based on selected year and month/quarter
    // When 'Todos' or a quarter is selected, this method aggregates expenses by description
    // and returns the top 10 highest expenses
    filteredExpensesData() {
      if (!this.dashboardData || !this.dashboardData.gastosIngresos) {
        return [];
      }

      // Get all expenses (is_gasto = true)
      const allExpenses = this.dashboardData.gastosIngresos.filter(item => item.is_gasto);

      // Si hay múltiples ejercicios, los datos ya vienen filtrados del padre - NO filtrar más
      let filteredData;
      if (this.hasMultipleFiscalYears) {
        filteredData = allExpenses;
      } else {
        // Filter expenses data by selected global fiscal year and month/quarter
        filteredData = allExpenses.filter(item => {
          // Check fiscal year if selected
          if (this.selectedFiscalYear !== null) {
            // Extract year from mes_ejercicio (format: "month/year" or "m/yyyy")
            if (!item.mes_ejercicio || typeof item.mes_ejercicio !== 'string') {
              return false;
            }

            const parts = item.mes_ejercicio.split('/');
            if (parts.length !== 2) {
              return false;
            }

            const year = parseInt(parts[1]);
            if (isNaN(year) || year !== this.selectedFiscalYear) {
              return false;
            }
          }

          // Check month/quarter if selected
          if (this.selectedMonth !== 'todos') {
            if (!item.mes_ejercicio || typeof item.mes_ejercicio !== 'string') {
              return false;
            }

            return this.monthMatchesFilter(item.mes_ejercicio);
          }

          // If 'todos' is selected for month, include all items that match the year
          return true;
        });
      }
      
      // If 'todos' or a quarter is selected, aggregate the results by description
      if (this.selectedMonth === 'todos' || 
          this.selectedMonth === 'q1' || 
          this.selectedMonth === 'q2' || 
          this.selectedMonth === 'q3' || 
          this.selectedMonth === 'q4') {
        
        // Group by description and sum the totals
        const aggregatedData = [];
        const groupedByDescription = {};
        
        filteredData.forEach(item => {
          if (!groupedByDescription[item.descripcion]) {
            groupedByDescription[item.descripcion] = {
              mes_ejercicio: item.mes_ejercicio, // Keep the first month for reference
              is_gasto: item.is_gasto,
              descripcion: item.descripcion,
              total: 0,
              cuenta: item.cuenta
            };
            aggregatedData.push(groupedByDescription[item.descripcion]);
          }
          
          groupedByDescription[item.descripcion].total += item.total;
        });
        
        // Sort by total in descending order and limit to top 10
        return aggregatedData
          .sort((a, b) => b.total - a.total)
          .slice(0, 10);
      }
      
      // For individual months, just sort and limit to top 10
      return filteredData
        .sort((a, b) => b.total - a.total)
        .slice(0, 10);
    },
    
    // Filtered income data based on selected year and month/quarter
    // When 'Todos' or a quarter is selected, this method aggregates income by description
    // and returns the top 10 highest income sources
    filteredIncomeData() {
      if (!this.dashboardData || !this.dashboardData.gastosIngresos) {
        return [];
      }

      // Get all income items (is_gasto = false)
      const allIncome = this.dashboardData.gastosIngresos.filter(item => !item.is_gasto);

      // Si hay múltiples ejercicios, los datos ya vienen filtrados del padre - NO filtrar más
      let filteredData;
      if (this.hasMultipleFiscalYears) {
        filteredData = allIncome;
      } else {
        // Filter income data by selected global fiscal year and month/quarter
        filteredData = allIncome.filter(item => {
          // Check fiscal year if selected
          if (this.selectedFiscalYear !== null) {
            // Extract year from mes_ejercicio (format: "month/year" or "m/yyyy")
            if (!item.mes_ejercicio || typeof item.mes_ejercicio !== 'string') {
              return false;
            }

            const parts = item.mes_ejercicio.split('/');
            if (parts.length !== 2) {
              return false;
            }

            const year = parseInt(parts[1]);
            if (isNaN(year) || year !== this.selectedFiscalYear) {
              return false;
            }
          }

          // Check month/quarter if selected
          if (this.selectedMonth !== 'todos') {
            if (!item.mes_ejercicio || typeof item.mes_ejercicio !== 'string') {
              return false;
            }

            return this.monthMatchesFilter(item.mes_ejercicio);
          }

          // If 'todos' is selected for month, include all items that match the year
          return true;
        });
      }
      
      // If 'todos' or a quarter is selected, aggregate the results by description
      if (this.selectedMonth === 'todos' || 
          this.selectedMonth === 'q1' || 
          this.selectedMonth === 'q2' || 
          this.selectedMonth === 'q3' || 
          this.selectedMonth === 'q4') {
        
        // Group by description and sum the totals
        const aggregatedData = [];
        const groupedByDescription = {};
        
        filteredData.forEach(item => {
          if (!groupedByDescription[item.descripcion]) {
            groupedByDescription[item.descripcion] = {
              mes_ejercicio: item.mes_ejercicio, // Keep the first month for reference
              is_gasto: item.is_gasto,
              descripcion: item.descripcion,
              total: 0,
              cuenta: item.cuenta
            };
            aggregatedData.push(groupedByDescription[item.descripcion]);
          }
          
          groupedByDescription[item.descripcion].total += item.total;
        });
        
        // Sort by total in descending order and limit to top 10
        return aggregatedData
          .sort((a, b) => b.total - a.total)
          .slice(0, 10);
      }
      
      // For individual months, just sort and limit to top 10
      return filteredData
        .sort((a, b) => b.total - a.total)
        .slice(0, 10);
    },
    
    // Filtered clientes proveedores expenses data based on selected year and month/quarter
    filteredClientesProveedoresExpensesData() {
      if (!this.dashboardData || !this.dashboardData.clientesProveedores) {
        return [];
      }

      // Get all expenses (is_gasto = true)
      const allExpenses = this.dashboardData.clientesProveedores.filter(item => item.is_gasto);

      // Si hay múltiples ejercicios, los datos ya vienen filtrados del padre - NO filtrar más
      let filteredData;
      if (this.hasMultipleFiscalYears) {
        filteredData = allExpenses;
      } else {
        // Filter expenses data by selected global fiscal year and month/quarter
        filteredData = allExpenses.filter(item => {
          // Check fiscal year if selected
          if (this.selectedFiscalYear !== null) {
            // Extract year from mes_ejercicio (format: "month/year" or "m/yyyy")
            if (!item.mes_ejercicio || typeof item.mes_ejercicio !== 'string') {
              return false;
            }

            const parts = item.mes_ejercicio.split('/');
            if (parts.length !== 2) {
              return false;
            }

            const year = parseInt(parts[1]);
            if (isNaN(year) || year !== this.selectedFiscalYear) {
              return false;
            }
          }

          // Check month/quarter if selected
          if (this.selectedMonth !== 'todos') {
            if (!item.mes_ejercicio || typeof item.mes_ejercicio !== 'string') {
              return false;
            }

            return this.monthMatchesFilter(item.mes_ejercicio);
          }

          // If 'todos' is selected for month, include all items that match the year
          return true;
        });
      }
      
      // If 'todos' or a quarter is selected, aggregate the results by description
      if (this.selectedMonth === 'todos' || 
          this.selectedMonth === 'q1' || 
          this.selectedMonth === 'q2' || 
          this.selectedMonth === 'q3' || 
          this.selectedMonth === 'q4') {
        
        // Group by description and sum the totals
        const aggregatedData = [];
        const groupedByDescription = {};
        
        filteredData.forEach(item => {
          if (!groupedByDescription[item.descripcion]) {
            groupedByDescription[item.descripcion] = {
              mes_ejercicio: item.mes_ejercicio, // Keep the first month for reference
              is_gasto: item.is_gasto,
              descripcion: item.descripcion,
              total: 0,
              cuenta: item.cuenta
            };
            aggregatedData.push(groupedByDescription[item.descripcion]);
          }
          
          groupedByDescription[item.descripcion].total += item.total;
        });
        
        // Sort by total in descending order and limit to top 10
        return aggregatedData
          .sort((a, b) => b.total - a.total)
          .slice(0, 10);
      }
      
      // For individual months, just sort and limit to top 10
      return filteredData
        .sort((a, b) => b.total - a.total)
        .slice(0, 10);
    },
    
    // Filtered clientes proveedores income data based on selected year and month/quarter
    filteredClientesProveedoresIncomeData() {
      if (!this.dashboardData || !this.dashboardData.clientesProveedores) {
        return [];
      }

      // Get all income items (is_gasto = false)
      const allIncome = this.dashboardData.clientesProveedores.filter(item => !item.is_gasto);

      // Si hay múltiples ejercicios, los datos ya vienen filtrados del padre - NO filtrar más
      let filteredData;
      if (this.hasMultipleFiscalYears) {
        filteredData = allIncome;
      } else {
        // Filter income data by selected global fiscal year and month/quarter
        filteredData = allIncome.filter(item => {
          // Check fiscal year if selected
          if (this.selectedFiscalYear !== null) {
            // Extract year from mes_ejercicio (format: "month/year" or "m/yyyy")
            if (!item.mes_ejercicio || typeof item.mes_ejercicio !== 'string') {
              return false;
            }

            const parts = item.mes_ejercicio.split('/');
            if (parts.length !== 2) {
              return false;
            }

            const year = parseInt(parts[1]);
            if (isNaN(year) || year !== this.selectedFiscalYear) {
              return false;
            }
          }

          // Check month/quarter if selected
          if (this.selectedMonth !== 'todos') {
            if (!item.mes_ejercicio || typeof item.mes_ejercicio !== 'string') {
              return false;
            }

            return this.monthMatchesFilter(item.mes_ejercicio);
          }

          // If 'todos' is selected for month, include all items that match the year
          return true;
        });
      }
      
      // If 'todos' or a quarter is selected, aggregate the results by description
      if (this.selectedMonth === 'todos' || 
          this.selectedMonth === 'q1' || 
          this.selectedMonth === 'q2' || 
          this.selectedMonth === 'q3' || 
          this.selectedMonth === 'q4') {
        
        // Group by description and sum the totals
        const aggregatedData = [];
        const groupedByDescription = {};
        
        filteredData.forEach(item => {
          if (!groupedByDescription[item.descripcion]) {
            groupedByDescription[item.descripcion] = {
              mes_ejercicio: item.mes_ejercicio, // Keep the first month for reference
              is_gasto: item.is_gasto,
              descripcion: item.descripcion,
              total: 0,
              cuenta: item.cuenta
            };
            aggregatedData.push(groupedByDescription[item.descripcion]);
          }
          
          groupedByDescription[item.descripcion].total += item.total;
        });
        
        // Sort by total in descending order and limit to top 10
        return aggregatedData
          .sort((a, b) => b.total - a.total)
          .slice(0, 10);
      }
      
      // For individual months, just sort and limit to top 10
      return filteredData
        .sort((a, b) => b.total - a.total)
        .slice(0, 10);
    },
    
    // Filtered monthly data based on selected global fiscal year
    filteredMonthlyData() {
      if (!this.dashboardData || !this.dashboardData.monthlyKpis) {
        return [];
      }

      // Si hay múltiples ejercicios, los datos ya vienen filtrados del padre - NO filtrar más
      if (this.hasMultipleFiscalYears) {
        return this.dashboardData.monthlyKpis;
      }

      // If no fiscal year is selected, return all data
      if (this.selectedFiscalYear === null) {
        return this.dashboardData.monthlyKpis;
      }

      // Filter monthly data by selected global fiscal year
      return this.dashboardData.monthlyKpis.filter(item => {
        const [, year] = item.mes_ejercicio.split('/');
        return parseInt(year) === this.selectedFiscalYear;
      });
    },

    // Colors for charts
    chartColors() {
      const colors = {
        ingresos: '#4CAF50', // Green for Ingresos
        gastos: '#F44336',   // Red for Gastos
        beneficio: '#2196F3' // Blue for Beneficio
      };
      
      // Return colors based on the selected view
      if (this.chartView === 'ingresos') {
        return [colors.ingresos];
      } else if (this.chartView === 'gastos') {
        return [colors.gastos];
      } else if (this.chartView === 'beneficio') {
        return [colors.beneficio];
      } else {
        // 'todos' view - return all colors in the correct order
        return [colors.ingresos, colors.gastos, colors.beneficio];
      }
    },
    
    // Monthly Evolution Chart
    monthlyChartOptions() {
      return {
        chart: {
          type: 'line',
          toolbar: {
            show: true
          },
          foreColor: '#333',
          background: 'transparent'
        },
        stroke: {
          curve: 'smooth',
          width: 3
        },
        xaxis: {
          categories: this.filteredMonthlyData.map(item => {
            const [month, year] = item.mes_ejercicio.split('/');
            return `${this.getMonthName(parseInt(month))} ${year}`;
          }),
          labels: {
            style: {
              colors: '#333'
            }
          }
        },
        yaxis: {
          labels: {
            style: {
              colors: '#333'
            },
            formatter: (value) => {
              return this.formatCurrency(value, false);
            }
          }
        },
        grid: {
          borderColor: '#ddd',
          row: {
            colors: ['#f8f9fa', '#ffffff']
          }
        },
        colors: this.chartColors,
        tooltip: {
          theme: 'light',
          y: {
            formatter: (value) => this.formatCurrency(value)
          }
        },
        legend: {
          position: 'top',
          labels: {
            colors: '#333'
          }
        }
      };
    },

    monthlyChartSeries() {
      if (!this.filteredMonthlyData.length) return [];

      const series = [];

      if (this.chartView === 'ingresos' || this.chartView === 'todos') {
        series.push({
          name: 'Ingresos',
          data: this.filteredMonthlyData.map(item => item.ingresos)
        });
      }

      if (this.chartView === 'gastos' || this.chartView === 'todos') {
        series.push({
          name: 'Gastos',
          data: this.filteredMonthlyData.map(item => item.gastos)
        });
      }

      if (this.chartView === 'beneficio' || this.chartView === 'todos') {
        series.push({
          name: 'Beneficio',
          data: this.filteredMonthlyData.map(item => item.beneficio)
        });
      }

      return series;
    },

    // Expenses Pie Chart
    expensesChartOptions() {
      return {
        chart: {
          type: 'pie',
          toolbar: {
            show: true
          },
          foreColor: '#333',
          background: 'transparent'
        },
        labels: this.filteredExpensesData.map(item => item.descripcion),
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              width: 300
            },
            legend: {
              position: 'bottom'
            }
          }
        }],
        tooltip: {
          theme: 'light',
          y: {
            formatter: (value, { dataPointIndex }) => {
              // Get the corresponding item from filteredExpensesData
              const item = this.filteredExpensesData[dataPointIndex];
              if (item) {
                return `<div style="padding: 5px;">
                  <div style="font-weight: bold; margin-bottom: 5px;">${item.descripcion}</div>
                  <div style="margin-bottom: 3px;">Importe: ${this.formatCurrency(item.total)}</div>
                  <div>Cuenta: ${item.cuenta}</div>
                </div>`;
              }
              return this.formatCurrency(value);
            }
          }
        },
        legend: {
          labels: {
            colors: '#333'
          }
        },
        dataLabels: {
          style: {
            colors: ['#fff']
          }
        },
        title: {
          text: '',
          align: 'center',
          style: {
            fontSize: '14px',
            fontWeight: 'normal',
            color: '#666'
          }
        }
      };
    },

    expensesChartSeries() {
      return this.filteredExpensesData.map(item => item.total);
    },

    // Income Bar Chart
    incomeChartOptions() {
      return {
        chart: {
          type: 'bar',
          toolbar: {
            show: true
          },
          foreColor: '#333',
          background: 'transparent'
        },
        plotOptions: {
          bar: {
            horizontal: true,
            distributed: true,
            dataLabels: {
              position: 'top'
            }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: (val) => this.formatCurrency(val, false),
          offsetX: 30,
          style: {
            colors: ['#333']
          }
        },
        xaxis: {
          categories: this.filteredIncomeData.map(item => item.descripcion),
          labels: {
            style: {
              colors: '#333'
            },
            formatter: (value) => this.formatCurrency(value, false)
          }
        },
        grid: {
          borderColor: '#ddd',
          row: {
            colors: ['#f8f9fa', '#ffffff']
          }
        },
        colors: ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#607D8B'],
        tooltip: {
          theme: 'light',
          custom: ({ dataPointIndex }) => {
            const item = this.filteredIncomeData[dataPointIndex];
            return `
              <div class="apexcharts-tooltip-box">
                <div class="apexcharts-tooltip-title">${item.descripcion}</div>
                <div>Importe: ${this.formatCurrency(item.total)}</div>
                <div>Cuenta: ${item.cuenta}</div>
              </div>
            `;
          }
        },
        legend: {
          labels: {
            colors: '#333'
          }
        },
        title: {
          text: '',
          align: 'center',
          style: {
            fontSize: '14px',
            fontWeight: 'normal',
            color: '#666'
          }
        }
      };
    },

    incomeChartSeries() {
      const data = this.filteredIncomeData.map(item => item.total);
      return [{
        name: 'Ingresos',
        data: data
      }];
    },

    // Clientes Proveedores Expenses Pie Chart
    clientesProveedoresExpensesChartOptions() {
      return {
        chart: {
          type: 'pie',
          toolbar: {
            show: true
          },
          foreColor: '#333',
          background: 'transparent'
        },
        labels: this.filteredClientesProveedoresExpensesData.map(item => item.descripcion),
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              width: 300
            },
            legend: {
              position: 'bottom'
            }
          }
        }],
        tooltip: {
          theme: 'light',
          y: {
            formatter: (value, { dataPointIndex }) => {
              // Get the corresponding item from filteredClientesProveedoresExpensesData
              const item = this.filteredClientesProveedoresExpensesData[dataPointIndex];
              if (item) {
                return `<div style="padding: 5px;">
                  <div style="font-weight: bold; margin-bottom: 5px;">${item.descripcion}</div>
                  <div style="margin-bottom: 3px;">Importe: ${this.formatCurrency(item.total)}</div>
                  <div>Cuenta: ${item.cuenta}</div>
                </div>`;
              }
              return this.formatCurrency(value);
            }
          }
        },
        legend: {
          labels: {
            colors: '#333'
          }
        },
        dataLabels: {
          style: {
            colors: ['#fff']
          }
        },
        title: {
          text: '',
          align: 'center',
          style: {
            fontSize: '14px',
            fontWeight: 'normal',
            color: '#666'
          }
        }
      };
    },

    clientesProveedoresExpensesChartSeries() {
      return this.filteredClientesProveedoresExpensesData.map(item => item.total);
    },

    // Clientes Proveedores Income Bar Chart
    clientesProveedoresIncomeChartOptions() {
      return {
        chart: {
          type: 'bar',
          toolbar: {
            show: true
          },
          foreColor: '#333',
          background: 'transparent'
        },
        plotOptions: {
          bar: {
            horizontal: true,
            distributed: true,
            dataLabels: {
              position: 'top'
            }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: (val) => this.formatCurrency(val, false),
          offsetX: 30,
          style: {
            colors: ['#333']
          }
        },
        xaxis: {
          categories: this.filteredClientesProveedoresIncomeData.map(item => item.descripcion),
          labels: {
            style: {
              colors: '#333'
            },
            formatter: (value) => this.formatCurrency(value, false)
          }
        },
        grid: {
          borderColor: '#ddd',
          row: {
            colors: ['#f8f9fa', '#ffffff']
          }
        },
        colors: ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#607D8B'],
        tooltip: {
          theme: 'light',
          custom: ({ dataPointIndex }) => {
            const item = this.filteredClientesProveedoresIncomeData[dataPointIndex];
            return `
              <div class="apexcharts-tooltip-box">
                <div class="apexcharts-tooltip-title">${item.descripcion}</div>
                <div>Importe: ${this.formatCurrency(item.total)}</div>
                <div>Cuenta: ${item.cuenta}</div>
              </div>
            `;
          }
        },
        legend: {
          labels: {
            colors: '#333'
          }
        },
        title: {
          text: '',
          align: 'center',
          style: {
            fontSize: '14px',
            fontWeight: 'normal',
            color: '#666'
          }
        }
      };
    },

    clientesProveedoresIncomeChartSeries() {
      const data = this.filteredClientesProveedoresIncomeData.map(item => item.total);
      return [{
        name: 'Ingresos',
        data: data
      }];
    }
  },

  methods: {
    // Helper method to check if a month matches the selected month/quarter filter
    monthMatchesFilter(monthStr) {
      // If 'todos' is selected, return true for all months
      if (this.selectedMonth === 'todos') {
        return true;
      }
      
      // Check if monthStr is a valid string
      if (!monthStr || typeof monthStr !== 'string') {
        return false;
      }
      
      try {
        // Parse the month from the mes_ejercicio string (format: "month/year")
        const parts = monthStr.split('/');
        
        if (parts.length !== 2) {
          return false;
        }
        
        const month = parseInt(parts[0]);
        
        if (isNaN(month) || month < 1 || month > 12) {
          return false;
        }
        
        // Check for quarters
        if (this.selectedMonth === 'q1') {
          return month >= 1 && month <= 3;
        } else if (this.selectedMonth === 'q2') {
          return month >= 4 && month <= 6;
        } else if (this.selectedMonth === 'q3') {
          return month >= 7 && month <= 9;
        } else if (this.selectedMonth === 'q4') {
          return month >= 10 && month <= 12;
        }
        
        // Check for specific month
        const selectedMonth = parseInt(this.selectedMonth);
        return !isNaN(selectedMonth) && month === selectedMonth;
      } catch (error) {
        console.error('Error in monthMatchesFilter:', error, 'for monthStr:', monthStr);
        return false;
      }
    },

    formatCurrency(value, includeSymbol = true) {
      // Handle null, undefined, NaN, or invalid values
      if (value === null || value === undefined || isNaN(value) || !Number.isFinite(Number(value))) {
        return includeSymbol ? '0,00 €' : '0,00';
      }
      
      try {
        const formatter = new Intl.NumberFormat('es-ES', {
          style: includeSymbol ? 'currency' : 'decimal',
          currency: 'EUR',
          minimumFractionDigits: 2
        });
        return formatter.format(value);
      } catch (error) {
        console.error('Error formatting currency value:', value, error);
        return includeSymbol ? '0,00 €' : '0,00';
      }
    },

    getMonthName(month) {
      const months = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
      ];
      return months[month - 1];
    },

    selectRow(item) {
      // Navigate to document detail page with the document ID
      this.$router.push({
        name: "document-detail",
        params: { document: item.id }
      });
    },
    
    getTipoColor(tipo) {
      switch (tipo.toLowerCase()) {
        case 'emitida':
          return 'green'; // Color for Client invoices
        case 'recibida':
          return 'blue';  // Color for Supplier invoices
        case 'cliente':
          return 'purple'; // Additional color for 'cliente' if it exists
        case 'proveedor':
          return 'orange'; // Additional color for 'proveedor' if it exists
        default:
          return 'grey';  // Default color for unknown types
      }
    }
  }
}
</script>

<style scoped>
.dashboard-container {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  padding: 20px;
}

h1 {
  font-size: 2em;
  font-weight: 300;
  margin-bottom: 30px;
  color: #333;
}

.dashboard-kpis-fallback-card {
  border-radius: 12px;
  -webkit-box-shadow: none;
  box-shadow: none;
  overflow: visible;
  height: auto;
  padding-bottom: 20px;
  background-color: var(--v-neutral-base);
  margin-bottom: 30px;
}

.dashboard-kpis-fallback-card .v-card__title {
  color: var(--v-primary-base);
  font-size: 20px;
  font-weight: 500;
  border-bottom: none;
  padding-bottom: 10px;
}

.kpi-card {
  transition: transform 0.3s;
  height: 100%;
}

.kpi-card:hover {
  transform: translateY(-5px);
}

.kpi-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  margin-bottom: 15px;
}

.kpi-title {
  font-size: 1.1em;
  color: #666;
  margin-bottom: 10px;
}

.kpi-value {
  font-size: 1.8em;
  font-weight: 500;
  color: #333;
}

/* Chart styles to ensure equal heights */
.chart-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.chart-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 400px;
  max-height: 450px;
  padding: 0;
  overflow: hidden;
}

.chart-element {
  width: 100%;
  height: 350px;
  max-height: 350px;
  overflow: visible;
}

/* Year selector dropdown styling */
.year-selector {
  max-width: 150px;
  margin-left: 10px;
}

/* Make the dropdown text more visible */
.v-select.year-selector .v-select__selection,
.v-select.global-year-selector .v-select__selection {
  font-weight: 500;
  color: #333;
}

/* Global year selector styling */
.global-year-selector {
  max-width: 180px;
  margin-left: 16px;
}

/* Global month selector styling */
.global-month-selector {
  max-width: 180px;
}

/* Style for the fiscal year indicator in card titles */
.subtitle-1.ml-2 {
  color: #666;
  font-weight: normal;
}

/* Inner dashboard card styling */
.inner-dashboard-card {
  margin-bottom: 20px;
  border: 1px solid #e0e0e0;
  background-color: #ffffff;
}

.inner-dashboard-card .v-card__title {
  color: var(--v-primary-base);
  font-size: 18px;
  font-weight: 500;
  border-bottom: 1px solid #e6e6e6;
  padding-bottom: 15px;
}

/* Add padding to the main card's content area */
.dashboard-kpis-fallback-card > .v-card__text {
  padding: 20px;
}

.dashboard-kpis-fallback-card {
  height: auto;
  min-height: 480px;
  border-radius: 12px;
  -webkit-box-shadow: none;
  box-shadow: none;
  overflow: visible;
}
.dashboard-kpis-fallback-card .v-card__title {
  color: var(--v-primary-base);
  font-size: 20px;
  font-weight: 500;
  border-bottom: 1px solid #e6e6e6;
  padding-bottom: 30px;
}

.dashboard-kpis-fallback-card .centered-content {
  text-align: center;
  width: 98%;
  margin: 0;
  position: absolute;
  top: 50%;
  -ms-transform: translateY(-50%);
  transform: translateY(-50%);
}

/* No data message styling */
.no-data-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 350px;
  padding: 20px;
  text-align: center;
  background-color: var(--v-neutral-base);
  border-radius: 8px;
  border: 1px dashed #e0e0e0;
}

.no-data-message .v-icon {
  margin-bottom: 16px;
  opacity: 0.7;
}

.no-data-text {
  color: #757575;
  font-size: 16px;
  font-weight: 400;
  max-width: 80%;
  margin: 0 auto;
}

.warning-text {
  font-size: 13px !important;
  width: 100%;
  margin: 0 !important;
  padding-top: 5px !important;
  padding-bottom: 5px !important;
  border-top: 1px solid #e6e6e6;
}

/* Tabs styling */
.dashboard-tabs {
  border-bottom: 1px solid #e6e6e6;
  margin-top: 10px;
}

.dashboard-tabs .v-tab {
  font-weight: 500;
  text-transform: none;
  letter-spacing: normal;
}

.dashboard-tabs .v-tab--active {
  color: var(--v-primary-base);
}

</style>